import { type GetProps, type Menu, Spin } from "antd";
import { PatientLabel } from "./PatientLabel";
import type { ExistingPatientResult } from "api/responses";

type MenuProps = GetProps<typeof Menu>;

function getIdString(prp: ExistingPatientResult) {
    const idString = prp.id ? prp.id.toString() : prp.pmsId;
    if (!idString) throw new Error(`P/RP '${prp.familyName}' missing identifier`);
    return idString;
}

/**
 * Provides a menu of options for the user to choose when they want to manipulate a Patient/Responsible Party.
 * @param isFetching Indicates whether the results are still loading.
 * @param results The results of the user's search query.
 * @param onSelectExisting Callback invoked if the user selects an existing P/RP to manipulate.
 * @returns Menu specification properties.
 */
export function searchPatientMenu(
    isFetching: boolean,
    results: ExistingPatientResult[] | null | undefined,
    onSelectExisting: (selected: ExistingPatientResult) => void,
): MenuProps {
    const items: MenuProps["items"] =
        results && results.length > 0 ? results.map(prpResult) : [emptyResult(isFetching)];

    const onClick: MenuProps["onClick"] = ({ key }) => {
        const selectedPatient = results?.find((prp) => getIdString(prp) === key);
        if (selectedPatient) {
            onSelectExisting(selectedPatient);
        }
    };

    return { items, onClick };
}

function prpResult(prp: ExistingPatientResult) {
    return { key: getIdString(prp), label: <PatientLabel prp={prp} /> };
}

function emptyResult(isFetching: boolean) {
    return {
        key: "no-results",
        disabled: true,
        label: isFetching ? <Spin>Loading...</Spin> : "No matching results",
    };
}
